import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import Logo from '../../assets/sysfar-logo.png';
import { login, sendResetPasswordEmail } from '../../redux/user/user.actions';
import { Grid, TextField, Button, Typography, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import styles from './login.module.css';
import Spinner from '../../components/Spinner';

const Login = (props) => {
    const theme = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [hasError, setHasError] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [resetPasswordModal, setResetPasswordModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [resetError, setResetError] = useState('');
    const [waitTime, setWaitTime] = useState(0);
    const [isWaiting, setIsWaiting] = useState(false);

    const handleLogin = useCallback(e => {
        e.preventDefault();

        if (email && password) {
            props.login(email, password, rememberMe);
        } else {
            setHasError(true);
        }
    }, [email, password, rememberMe, props]);

    const handleOpenResetPassword = () => {
        setResetEmail(email); // Preserva o email atual
        setResetPasswordModal(true);
        setResetMessage('');
        setResetError('');
        setWaitTime(0);
        setIsWaiting(false);
    };

    const handleCloseResetPassword = () => {
        setResetPasswordModal(false);
        setResetEmail('');
        setResetMessage('');
        setResetError('');
        setWaitTime(0);
        setIsWaiting(false);
    };

    const startWaitTimer = (seconds = 60) => {
        setWaitTime(seconds);
        setIsWaiting(true);

        const timer = setInterval(() => {
            setWaitTime(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setIsWaiting(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResetPassword = () => {
        if (!resetEmail) {
            setResetError('Por favor, digite um email válido');
            return;
        }

        props.sendResetPasswordEmail(resetEmail);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                handleLogin(e);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [email, password, handleLogin]);

    // Efeito para lidar com as respostas do reset de senha
    useEffect(() => {
        if (props.updateSuccess === 'emailSent') {
            setResetMessage('Email enviado com sucesso! Verifique sua caixa de entrada.');
            setResetError('');
            localStorage.setItem('recoverEmail', resetEmail);
        } else if (props.authError && props.authError.errorMessage && props.authError.errorMessage.email) {
            const errorMessages = {
                "We can't find a user with that email address.": "Operador não encontrado",
                "Please wait before retrying.": "Aguarde antes de tentar novamente",
            };
            const errorMessage = errorMessages[props.authError.errorMessage.email] || props.authError.errorMessage.email;
            setResetError(errorMessage);
            setResetMessage('');

            // Se for erro de throttle, inicia o contador
            if (props.authError.errorMessage.email === "Please wait before retrying.") {
                startWaitTimer(60);
            }
        }
    }, [props.updateSuccess, props.authError, resetEmail]);

    return (
        <Grid container className={styles.loginContainer}>
            {/* Lado Esquerdo - Imagem */}
            <Grid item xs={12} md={6} className={styles.imageSection}>
                <div className={styles.imageContent}>
                    <img src={Logo} alt="Alodesk" className={styles.logo} />
                    <Typography variant="h4" className={styles.welcomeTitle}>
                        Bem-vindo ao Alodesk
                    </Typography>
                    <Typography variant="body1" className={styles.welcomeSubtitle}>
                        Sua plataforma de atendimento ao cliente
                    </Typography>
                </div>
            </Grid>

            {/* Lado Direito - Formulário */}
            <Grid item xs={12} md={6} className={styles.formSection} sx={{ backgroundColor: theme.palette.background.default }}>
                <div className={styles.formContainer} style={{ backgroundColor: theme.palette.background.paper }}>
                    <Typography variant="h5" className={styles.formTitle} sx={{ color: theme.palette.text.primary }}>
                        Faça login em sua conta
                    </Typography>
                    <Typography variant="body2" className={styles.formSubtitle} sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        Para continuar é necessário que identifique-se
                    </Typography>

                    <Spinner />

                    {props.authError && (
                        <Alert severity="error" sx={{ marginBottom: 2 }}>
                            {props.authError === 'invalid_credentials'
                                ? 'Email ou senha inválidos'
                                : typeof props.authError === 'string'
                                    ? props.authError
                                    : 'Erro interno do servidor. Tente novamente.'}
                        </Alert>
                    )}

                    <form onSubmit={handleLogin} className={styles.form}>
                        <TextField
                            variant="outlined"
                            label="Email"
                            type="email"
                            fullWidth
                            value={email}
                            onChange={({ target }) => setEmail(target.value)}
                            error={hasError && !email}
                            helperText={hasError && !email ? "O email é obrigatório" : ""}
                            sx={{ marginBottom: 2 }}
                        />

                        <TextField
                            variant="outlined"
                            label="Senha"
                            type="password"
                            fullWidth
                            value={password}
                            onChange={({ target }) => setPassword(target.value)}
                            error={hasError && !password}
                            helperText={hasError && !password ? "A senha é obrigatória" : ""}
                            sx={{ marginBottom: 2 }}
                        />

                        <div className={styles.formOptions}>
                            <div className={styles.checkboxContainer}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={() => setRememberMe(!rememberMe)}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                '&.Mui-checked': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main + '20',
                                                },
                                                '&.Mui-checked:hover': {
                                                    backgroundColor: theme.palette.primary.main + '20',
                                                }
                                            }}
                                        />
                                    }
                                    label="Manter conectado"
                                />
                            </div>
                            <Typography
                                component="button"
                                type="button"
                                onClick={handleOpenResetPassword}
                                className={styles.forgotPasswordLink}
                            >
                                Esqueceu a senha?
                            </Typography>
                        </div>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            className={styles.loginButton}
                        >
                            Entrar
                        </Button>
                    </form>
                </div>
            </Grid>

            {/* Modal de Redefinição de Senha */}
            <Dialog
                open={resetPasswordModal}
                onClose={handleCloseResetPassword}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Redefinir Senha
                    <IconButton onClick={handleCloseResetPassword} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        Digite seu email para receber as instruções de redefinição de senha.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        sx={{ marginTop: 1 }}
                        error={!!resetError}
                        helperText={resetError}
                    />
                    {resetMessage && (
                        <Alert severity="success" sx={{ marginTop: 2 }}>
                            {resetMessage}
                        </Alert>
                    )}
                    {isWaiting && (
                        <Alert severity="warning" sx={{ marginTop: 2 }}>
                            Aguarde {waitTime} segundos antes de tentar novamente.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResetPassword}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleResetPassword}
                        variant="contained"
                        disabled={isWaiting || !resetEmail.trim()}
                    >
                        {isWaiting ? `Aguarde ${waitTime}s` : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

const mapStateToProps = state => {
    const { user } = state;
    const { currentUser, authError, updateSuccess } = user;
    return { currentUser, authError, updateSuccess };
};

const mapDispatchToProps = dispatch => ({
    login: (email, password, rememberMe) => dispatch(login(email, password, rememberMe)),
    sendResetPasswordEmail: (email) => dispatch(sendResetPasswordEmail(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
